/**
 * BuilderHost — CRM-side SDK for managing a builder iframe.
 * 
 * Usage in CRM:
 * ```tsx
 * const host = new BuilderHost(iframeRef.current)
 * host.on('ready', () => { host.init({...}); host.load({...}) })
 * host.on('dirty', ({ isDirty }) => setHasUnsavedChanges(isDirty))
 * host.on('save', async (payload) => { await deploy(payload); host.saveAck({ success: true }) })
 * host.on('request-preview', async (payload) => { const session = await createPreview(payload); host.sendPreviewSession(session) })
 * ```
 */

import {
  PROTOCOL_VERSION,
  MESSAGE_PREFIX,
  isProtocolMessage,
  getMessageAction,
  type InitMessage,
  type LoadMessage,
  type PreviewSessionMessage,
  type SessionMessage,
  type ResourcesMessage,
  type RequestSaveMessage,
  type ViewportMessage,
  type SaveAckMessage,
  type StepConfigMessage,
  type FunnelContextMessage,
  type ReadyMessage,
  type DirtyMessage,
  type LoadFailedMessage,
  type SaveMessage,
  type RequestPreviewMessage,
  type RequestSessionMessage,
  type RequestResourcesMessage,
  type ResizeMessage,
  type MediaLibraryMessage,
  type MediaAssetRegisteredMessage,
  type RequestMediaLibraryMessage,
  type RegisterMediaAssetMessage,
  type NavigateStepMessage,
  type PatchStepConfigMessage,
  type RenderedMessage,
  type PicturesStoredMessage,
  type ThemeMessage,
  type ShortcutMessage,
  type AgentHostMessage,
  type AgentBuilderMessage,
} from './types'

// ─── Event Types ─────────────────────────────────────────────────────

export interface BuilderHostEvents {
  /** Builder is loaded and ready */
  'ready': ReadyMessage['payload']
  /** Builder has unsaved changes */
  'dirty': DirtyMessage['payload']
  /** Builder submitted save data (manifest, assets, sourceData) */
  'save': SaveMessage['payload']
  /** Builder requests a preview session with real resources */
  'request-preview': RequestPreviewMessage['payload']
  /** Builder asks for a fresh session token before its own expires */
  'request-session': RequestSessionMessage['payload']
  /** Builder requests store resources */
  'request-resources': RequestResourcesMessage['payload']
  /** Builder wants to close */
  'close': undefined
  /** Builder requests resize */
  'resize': ResizeMessage['payload']
  /** Builder requests media library listing */
  'request-media-library': RequestMediaLibraryMessage['payload']
  /** Builder wants to register a newly uploaded media asset */
  'register-media-asset': RegisterMediaAssetMessage['payload']
  /** Builder requests navigation to a different funnel step */
  'navigate-step': NavigateStepMessage['payload']
  /** Builder asks the host to write the step's commerce config */
  'patch-step-config': PatchStepConfigMessage['payload']
  /** Builder has drawn the loaded page or gallery */
  'rendered': RenderedMessage['payload']
  /** Builder could not open the page the host loaded */
  'load-failed': LoadFailedMessage['payload']
  /** Builder has stored new pictures of a design's page */
  'pictures-stored': PicturesStoredMessage['payload']
  /** The merchant pressed one of the host's shortcuts inside the frame */
  'shortcut': ShortcutMessage['payload']
  /** The builder answered or told the host's assistant something */
  'agent': AgentBuilderMessage['payload']
}

type EventHandler<T> = (payload: T) => void | Promise<void>

// ─── BuilderHost Class ───────────────────────────────────────────────

export class BuilderHost {
  private iframe: HTMLIFrameElement | null = null
  private targetOrigin: string
  private handlers = new Map<string, Set<EventHandler<any>>>()
  private messageHandler: ((event: MessageEvent) => void) | null = null
  private isDestroyed = false

  constructor(options?: { targetOrigin?: string }) {
    this.targetOrigin = options?.targetOrigin || '*'
  }

  /** Connect to an iframe element and start listening */
  connect(iframe: HTMLIFrameElement): void {
    this.iframe = iframe
    
    // Listen for messages from the builder
    this.messageHandler = (event: MessageEvent) => {
      // Only handle messages from THIS host's own iframe. Multiple
      // BuilderHosts can listen on the same window concurrently (e.g. the
      // parallel auto-deploy of several funnel steps, each in its own hidden
      // iframe). Without this guard, one iframe's `save` is delivered to every
      // host and cross-wires their results — every step would inherit the
      // first/only successful deployment. In single-frame usage event.source
      // is always this iframe, so behaviour is unchanged.
      if (this.iframe?.contentWindow && event.source !== this.iframe.contentWindow) return

      // Validate origin if specified
      if (this.targetOrigin !== '*' && event.origin !== this.targetOrigin) return

      if (!isProtocolMessage(event.data)) return
      
      const action = getMessageAction(event.data.type)
      const handlers = this.handlers.get(action)
      
      if (handlers) {
        const payload = 'payload' in event.data ? (event.data as any).payload : undefined
        handlers.forEach(handler => handler(payload))
      }
    }
    
    window.addEventListener('message', this.messageHandler)
  }

  /** Disconnect and stop listening */
  disconnect(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
      this.messageHandler = null
    }
    this.iframe = null
    this.isDestroyed = true
  }

  /** Register an event handler */
  on<K extends keyof BuilderHostEvents>(
    event: K, 
    handler: EventHandler<BuilderHostEvents[K]>
  ): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set())
    }
    this.handlers.get(event)!.add(handler)
    
    // Return unsubscribe function
    return () => {
      this.handlers.get(event)?.delete(handler)
    }
  }

  /** Send a message to the builder iframe */
  private send(message: Record<string, unknown>): void {
    if (this.isDestroyed || !this.iframe?.contentWindow) {
      console.warn('[BuilderHost] Cannot send message — iframe not connected')
      return
    }
    this.iframe.contentWindow.postMessage(message, this.targetOrigin)
  }

  // ─── Host → Builder Commands ─────────────────────────────────────

  /** Initialize the builder with store/funnel/step context */
  init(payload: InitMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:init`,
      version: PROTOCOL_VERSION,
      payload,
    } satisfies InitMessage)
  }

  /** Load existing content for editing */
  load(payload: LoadMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:load`,
      payload,
    } satisfies LoadMessage)
  }

  /** Send preview session tokens (response to request-preview) */
  sendPreviewSession(payload: PreviewSessionMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:preview-session`,
      payload,
    } satisfies PreviewSessionMessage)
  }

  /** Send a fresh session token, or the reason there is none (response to request-session) */
  sendSession(payload: SessionMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:session`,
      payload,
    } satisfies SessionMessage)
  }

  /** Send store resources (response to request-resources) */
  sendResources(payload: ResourcesMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:resources`,
      payload,
    } satisfies ResourcesMessage)
  }

  /** Request the builder to save */
  requestSave(): void {
    this.send({
      type: `${MESSAGE_PREFIX}:request-save`,
    } satisfies RequestSaveMessage)
  }

  /** Inform builder of viewport change */
  setViewport(payload: ViewportMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:viewport`,
      payload,
    } satisfies ViewportMessage)
  }

  /** Acknowledge save result */
  saveAck(payload: SaveAckMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:save-ack`,
      payload,
    } satisfies SaveAckMessage)
  }

  /** Push updated step configuration to the builder */
  sendStepConfig(
    stepConfig: Record<string, unknown> | null,
    /** Set when this answers a `patch-step-config`: the id to match, and why it was refused. */
    answer?: Omit<StepConfigMessage['payload'], 'stepConfig'>,
  ): void {
    this.send({
      type: `${MESSAGE_PREFIX}:step-config`,
      payload: { stepConfig, ...answer },
    } satisfies StepConfigMessage)
  }

  /** Send funnel context (steps, edges, current step) to the builder */
  sendFunnelContext(payload: FunnelContextMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:funnel-context`,
      payload,
    } satisfies FunnelContextMessage)
  }

  /** Tell the builder the host's colour scheme (see `ThemeMessage`) */
  setTheme(payload: ThemeMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:theme`,
      payload,
    } satisfies ThemeMessage)
  }

  /** Drive the builder for the host's assistant (see `AgentHostMessage`) */
  agent(payload: AgentHostMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:agent`,
      payload,
    } satisfies AgentHostMessage)
  }

  // ─── Media Library ──────────────────────────────────────────────

  /** Send media library listing (response to request-media-library) */
  sendMediaLibrary(payload: MediaLibraryMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:media-library`,
      payload,
    } satisfies MediaLibraryMessage)
  }

  /** Confirm media asset registration (response to register-media-asset) */
  sendMediaAssetRegistered(payload: MediaAssetRegisteredMessage['payload']): void {
    this.send({
      type: `${MESSAGE_PREFIX}:media-asset-registered`,
      payload,
    } satisfies MediaAssetRegisteredMessage)
  }
}
